<template>
  <div v-show="!!menu" ref="menuRef" class="rec-inline-options-menu" :style="menuStyle" @click.stop>
    <div
      v-for="option in menu ? menu.options : []"
      :key="option.value"
      class="rec-inline-options-menu__option"
      :class="{ 'rec-inline-options-menu__option--selected': menu && option.value === menu.currentValue }"
      role="button"
      @click="selectOption(option)"
    >
      {{ option.label }}
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, nextTick, onMounted, onBeforeUnmount } from '@vue/composition-api'

const VIEWPORT_MARGIN = 8

// Shared, single-instance replacement for the native <select> RecProductionReport's Variance
// Type/Admin Approval cells used to render - same portal-to-<body>/manual-positioning technique
// as RecColumnSearchMenu (see its own comments for the sticky-header/Popper positioning bug this
// avoids), reached for here for a different reason: a native <select>'s own option-list highlight
// color is rendered by the OS/browser itself and can't be restyled via CSS in Chrome/Edge
// (explicit decision, not an oversight, after confirming this) - this renders the option list as
// plain page markup instead, so it CAN be styled to match the rest of the report (in particular,
// the orange used for row selection in the tables, instead of the browser's native blue).
// One shared instance (not one per row/field) for the same reason RecColumnSearchMenu is
// singular: this open/closed state must NOT live as a ref on RecProductionReport itself - its
// Variance Transaction Details table's ~15 #cell() scoped slots aren't "stable" to bootstrap-vue's
// b-table, so any reactive change read during that component's own render forces a full
// re-render of every row/cell (profiled at 7-10s over ~100 rows the first time this exact class
// of bug was hit, via the column-search popover RecColumnSearchMenu itself replaced).
export default defineComponent({
  name: 'rec-inline-options-menu',
  setup() {
    // Only one row/field's menu can be open at a time, so a single object (or null) is enough -
    // no need for one ref per row/field. options/currentValue/onSelect are snapshotted from
    // whichever trigger called toggle() below, not read reactively from RecProductionReport.
    const menu = ref(null)
    const menuRef = ref(null)

    const menuStyle = computed(() =>
      menu.value
        ? {
            top: `${menu.value.top}px`,
            left: `${menu.value.left}px`,
            // Per-call override (see toggle's own widthPx param) - omitted entirely for every
            // caller that doesn't pass one, so the scoped .rec-inline-options-menu min-width/
            // max-width CSS keeps deciding their width exactly as before.
            ...(menu.value.widthPx ? { minWidth: `${menu.value.widthPx}px`, maxWidth: `${menu.value.widthPx}px` } : {}),
          }
        : {}
    )

    const close = () => {
      menu.value = null
    }

    // targetId identifies which trigger this call came from (e.g. `${rowKey}:variance_type`) -
    // clicking the SAME trigger again closes it, the same toggle behaviour a native <select>
    // gives for free. onSelect is a plain callback (not an emitted event) so the caller doesn't
    // need to track "which row/field is currently open" itself - RecProductionReport already has
    // that captured in the closure it passes in here. widthPx is optional (added by explicit
    // request, for BDXValidationReport.vue's own Claim Status column trigger, whose "All"/"Open"/
    // "Closed"/"Blank" options never need this menu's default 150-260px range) - every other
    // caller omits it and keeps that default entirely.
    const toggle = async (targetId, event, options, currentValue, onSelect, widthPx) => {
      if (menu.value && menu.value.targetId === targetId) {
        close()
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()
      menu.value = { targetId, options, currentValue, onSelect, widthPx, top: rect.bottom + 2, left: rect.left }

      await nextTick()

      if (!menuRef.value || !menu.value || menu.value.targetId !== targetId) return

      const menuRect = menuRef.value.getBoundingClientRect()
      const margin = VIEWPORT_MARGIN

      let left = rect.left
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin)

      let top = rect.bottom + 2
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2
      }
      top = Math.max(margin, top)

      menu.value = { ...menu.value, top, left }
    }

    const selectOption = (option) => {
      if (menu.value && menu.value.onSelect) menu.value.onSelect(option.value)
      close()
    }

    // Same mousedown/scroll/Escape handling as RecColumnSearchMenu - see its own comments.
    const onDocumentMousedown = (event) => {
      if (!menu.value) return
      if (menuRef.value && menuRef.value.contains(event.target)) return
      if (event.target.closest && event.target.closest('.fcp-inline-options-toggle')) return
      close()
    }

    const onScroll = () => {
      if (menu.value) close()
    }

    const onKeydown = (event) => {
      if ('Escape' === event.key && menu.value) close()
    }

    onMounted(() => {
      if (menuRef.value) document.body.appendChild(menuRef.value)
      document.addEventListener('mousedown', onDocumentMousedown)
      window.addEventListener('scroll', onScroll, true)
      document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount(() => {
      if (menuRef.value && document.body.contains(menuRef.value)) {
        document.body.removeChild(menuRef.value)
      }
      document.removeEventListener('mousedown', onDocumentMousedown)
      window.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('keydown', onKeydown)
    })

    return {
      menu,
      menuRef,
      menuStyle,
      toggle,
      selectOption
    }
  }
})
</script>

<style lang="scss" scoped>
.rec-inline-options-menu {
  position: fixed;
  z-index: 2000;
  min-width: 150px;
  max-width: 260px;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.2rem 0;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  font-size: 0.62rem;
  color: #000;

  &__option {
    padding: 0.25rem 0.6rem;
    cursor: pointer;
    white-space: nowrap;

    // Same orange used for row selection in the Variance/BDX/Cashbook tables
    // (.fcp-transaction-row--active) - explicitly replacing the browser's own
    // native blue option-list highlight, which isn't stylable at all (see
    // this file's own top comment).
    &:hover {
      background-color: #fde8cc;
    }

    &--selected {
      background-color: #fde8cc;
      font-weight: 600;
    }
  }
}
</style>
